/**
 * useHaptics Hook v1.1
 * 
 * Provides haptic feedback for iOS and Android devices
 * Uses global Capacitor Plugins instead of module imports
 * Falls back to web Vibration API on non-native platforms
 */

import { useCallback, useMemo } from 'react';

export type HapticFeedbackType =
    | 'light'      // Light tap - button presses
    | 'medium'     // Medium tap - toggles, selections
    | 'heavy'      // Heavy tap - important actions
    | 'success'    // Success notification - correct answer, completed action
    | 'warning'    // Warning notification - alerts
    | 'error';     // Error notification - wrong answer, failed action

export interface UseHapticsOptions {
    enabled?: boolean; // Allow disabling haptics
}

export interface UseHapticsReturn {
    trigger: (type: HapticFeedbackType) => void;
    light: () => void;
    medium: () => void;
    heavy: () => void;
    success: () => void;
    warning: () => void;
    error: () => void;
    selection: () => void;
    isSupported: boolean;
}

/**
 * Get Capacitor Haptics plugin from global Capacitor.Plugins
 * This avoids any module imports that would cause Vite errors
 */
function getHapticsPlugin(): any | null {
    try {
        const Capacitor = (window as any).Capacitor;
        if (Capacitor?.isNativePlatform?.() && Capacitor?.Plugins?.Haptics) {
            return Capacitor.Plugins.Haptics;
        }
    } catch {
        // Not in Capacitor context
    }
    return null;
}

/**
 * Detect if device supports haptic feedback
 */
function detectHapticsSupport(): boolean {
    if (typeof window === 'undefined') return false;

    // Check for Capacitor native
    const Capacitor = (window as any).Capacitor;
    if (Capacitor?.isNativePlatform?.()) {
        return true;
    }

    // Check for web Vibration API
    if ('vibrate' in navigator) {
        return true;
    }

    return false;
}

/**
 * useHaptics - Hook for haptic feedback
 * 
 * @example
 * const haptics = useHaptics();
 * 
 * // On button click
 * onClick={() => {
 *   haptics.light();
 *   // do action
 * }}
 * 
 * // On correct quiz answer
 * if (isCorrect) {
 *   haptics.success();
 * } else {
 *   haptics.error();
 * }
 */
export function useHaptics(options: UseHapticsOptions = {}): UseHapticsReturn {
    const { enabled = localStorage.getItem('luca_haptics_enabled') !== 'false' } = options;

    const isSupported = useMemo(() => detectHapticsSupport(), []);
    const isNative = useMemo(() => {
        return typeof window !== 'undefined' &&
            (window as any).Capacitor?.isNativePlatform?.();
    }, []);

    /**
     * Trigger impact haptic
     */
    const triggerImpact = useCallback(async (style: 'Light' | 'Medium' | 'Heavy') => {
        if (!enabled || !isSupported) return;

        try {
            if (isNative) {
                const Haptics = getHapticsPlugin();
                if (Haptics) {
                    await Haptics.impact({ style });
                }
            } else if ('vibrate' in navigator) {
                // Web fallback - use vibration pattern
                const duration = style === 'Light' ? 10 :
                    style === 'Medium' ? 20 : 30;
                navigator.vibrate(duration);
            }
        } catch (error) {
            // Silently fail - haptics are nice-to-have
            console.debug('[Haptics] Impact failed:', error);
        }
    }, [enabled, isSupported, isNative]);

    /**
     * Trigger notification haptic
     */
    const triggerNotification = useCallback(async (type: 'Success' | 'Warning' | 'Error') => {
        if (!enabled || !isSupported) return;

        try {
            if (isNative) {
                const Haptics = getHapticsPlugin();
                if (Haptics) {
                    await Haptics.notification({ type });
                }
            } else if ('vibrate' in navigator) {
                // Web fallback - use vibration patterns
                switch (type) {
                    case 'Success':
                        navigator.vibrate([10, 50, 10]); // Light double tap
                        break;
                    case 'Warning':
                        navigator.vibrate([20, 100, 20]); // Medium double tap
                        break;
                    case 'Error':
                        navigator.vibrate([30, 50, 30, 50, 30]); // Triple tap
                        break;
                }
            }
        } catch (error) {
            console.debug('[Haptics] Notification failed:', error);
        }
    }, [enabled, isSupported, isNative]);

    /**
     * Trigger selection change haptic
     */
    const triggerSelection = useCallback(async () => {
        if (!enabled || !isSupported) return;

        try {
            if (isNative) {
                const Haptics = getHapticsPlugin();
                if (Haptics) {
                    await Haptics.selectionChanged();
                }
            } else if ('vibrate' in navigator) {
                navigator.vibrate(5); // Very light
            }
        } catch (error) {
            console.debug('[Haptics] Selection failed:', error);
        }
    }, [enabled, isSupported, isNative]);

    /**
     * Main trigger function
     */
    const trigger = useCallback((type: HapticFeedbackType) => {
        switch (type) {
            case 'light':
                triggerImpact('Light');
                break;
            case 'medium':
                triggerImpact('Medium');
                break;
            case 'heavy':
                triggerImpact('Heavy');
                break;
            case 'success':
                triggerNotification('Success');
                break;
            case 'warning':
                triggerNotification('Warning');
                break;
            case 'error':
                triggerNotification('Error');
                break;
        }
    }, [triggerImpact, triggerNotification]);

    return {
        trigger,
        light: useCallback(() => triggerImpact('Light'), [triggerImpact]),
        medium: useCallback(() => triggerImpact('Medium'), [triggerImpact]),
        heavy: useCallback(() => triggerImpact('Heavy'), [triggerImpact]),
        success: useCallback(() => triggerNotification('Success'), [triggerNotification]),
        warning: useCallback(() => triggerNotification('Warning'), [triggerNotification]),
        error: useCallback(() => triggerNotification('Error'), [triggerNotification]),
        selection: triggerSelection,
        isSupported,
    };
}

export default useHaptics;
